import { Link } from '@tanstack/react-router';
import { useClientOrgs, useMe } from '@/api/hooks';
import type { ClientOrg } from '@/api/schemasMembers';
import { CompactTable, type CompactColumn } from '@/components/CompactTable';
import { EmptyState } from '@/components/EmptyState';
import { ErrorState } from '@/components/ErrorState';
import { Tag } from '@/components/badges';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { ClientProviders } from '@/features/clients/ClientProviders';
import { ClientStatusBadge } from '@/features/clients/ClientStatusBadge';
import { CreateClientDialog } from '@/features/clients/CreateClientDialog';
import { useClientRowActions } from '@/features/clients/useClientRowActions';
import { formatDate } from '@/lib/format';
import { useFeature } from '@/lib/permissions';

/**
 * Clients (platform operators, Malible staff): every client organization with its status and its Expo and Bitrise
 * accounts (which Hangar sets up itself, SPEC decision 19), New client to create one with its first admin's invitation,
 * the lifecycle actions of each status (SPEC decision 26: edit, invite, suspend or reactivate, delete once suspended)
 * and Preview to see a client's workspace as its team does, read-only (SPEC decision 22). The name opens the client
 * page. Hidden from the navigation for everyone else; the API refuses them anyway (403).
 */
export function ClientsPage() {
  const me = useMe();
  const served = useFeature('clients');
  const operator = me.data?.user?.platformRole === 'operator';
  const enabled = operator && served !== false;
  return (
    <div data-testid="clients-page" className="flex min-w-0 flex-col gap-5">
      <header className="enter flex flex-col gap-1 border-b pb-5">
        <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
          <h1 className="text-2xl font-semibold tracking-tight text-foreground">Clients</h1>
          {enabled ? <CreateClientDialog /> : null}
        </div>
        <p className="max-w-[72ch] text-sm text-muted-foreground">Each client is its own organization: its people, projects, apps and usage stay apart from every other client. Its first admin invites the rest of the team.</p>
      </header>
      {me.isPending ? <ClientsSkeleton /> : enabled ? <ClientsList /> : <NotOperator served={served} />}
    </div>
  );
}

function NotOperator({ served }: { served: boolean | undefined }) {
  return (
    <Card className="enter">
      <EmptyState
        testId="clients-locked"
        icon="lock"
        title={served === false ? 'Clients are not served by this Hangar yet' : 'Only platform operators manage clients'}
        description="Only Hangar's platform operators can create clients."
      />
    </Card>
  );
}

function ClientsSkeleton() {
  return (
    <div data-testid="clients-loading" className="flex flex-col gap-2" aria-busy="true" aria-label="Loading clients">
      {[0, 1, 2].map((i) => <Skeleton key={i} className="h-14 w-full" />)}
    </div>
  );
}

const columns: CompactColumn<ClientOrg>[] = [
  { key: 'name', header: 'Client', width: 'w-[28%]', className: 'max-w-0', cell: (o) => <OrgName o={o} /> },
  { key: 'status', header: 'Status', cell: (o) => <ClientStatusBadge status={o.status} testId={`client-status-${o.slug}`} /> },
  { key: 'domains', header: 'Email domains', hide: '@max-[52rem]:hidden', cell: (o) => <span className="font-mono text-xs text-muted-foreground">{o.emailDomains.join(', ') || '—'}</span> },
  { key: 'people', header: 'People', cell: (o) => <People o={o} /> },
  { key: 'providers', header: 'Providers', width: 'w-[28%]', className: 'max-w-0', hide: '@max-[44rem]:hidden', cell: (o) => <ClientProviders o={o} /> },
  { key: 'created', header: 'Created', hide: '@max-[36rem]:hidden', cell: (o) => <span className="text-xs text-muted-foreground">{formatDate(o.createdAt)}</span> },
];

function OrgName({ o }: { o: ClientOrg }) {
  return (
    <div className="min-w-0">
      <Link to="/clients/$orgId" params={{ orgId: o.id }} data-testid={`client-open-${o.slug}`} className="block truncate text-sm font-medium text-foreground underline-offset-2 hover:underline">{o.name}</Link>
      <div className="truncate font-mono text-xs text-subtle">{o.slug}</div>
    </div>
  );
}

function People({ o }: { o: ClientOrg }) {
  return (
    <span className="flex flex-wrap items-center gap-1.5">
      <Tag>{o.memberCount} {o.memberCount === 1 ? 'member' : 'members'}</Tag>
      {o.openInvitations ? <Tag className="text-awaiting">{o.openInvitations} invited</Tag> : null}
    </span>
  );
}

function ClientsList() {
  const orgs = useClientOrgs();
  const row = useClientRowActions();
  if (orgs.isPending) return <ClientsSkeleton />;
  if (orgs.isError) return <ErrorState error={orgs.error} onRetry={() => void orgs.refetch()} title="Couldn't load the clients" />;
  if (orgs.data.length === 0) {
    return (
      <Card className="enter">
        <EmptyState testId="clients-empty" icon="domain_add" title="No clients yet" description="Create the first client organization and invite its admin: they set up projects, Bitbucket and apps from there." action={<CreateClientDialog />} />
      </Card>
    );
  }
  return (
    <>
      <CompactTable
        label="Clients"
        rows={orgs.data}
        columns={columns}
        rowKey={(o) => o.id}
        rowTestId={(o) => `client-row-${o.slug}`}
        subject={(o) => o.name}
        actions={row.actions}
        actionsVariant="icon"
        phoneRow={(o) => (
          <div className="flex flex-col gap-2">
            <OrgName o={o} />
            <span className="flex flex-wrap items-center gap-1.5">
              <ClientStatusBadge status={o.status} />
              <People o={o} />
            </span>
            <ClientProviders o={o} />
          </div>
        )}
      />
      {row.dialogs}
    </>
  );
}
